/**
 * @file Public landing page — pages/Landing/ImpactPage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ImpactPage.css";

const ImpactPage: React.FC = () => {
  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Content Section */}
      <section className="impact-content">
        <h1 className="impact-title">Impact</h1>
        
        <div className="impact-section">
          <h2>Decarbonizing the Future of Construction</h2>
          <p>
            Embodied carbon accounts for a significant share of lifecycle emissions in buildings. This is especially critical in rapidly growing construction markets like India, where the scale of new infrastructure directly influences long-term climate trajectories.
          </p>
          <p>
            To manage this impact effectively, the industry requires data that is:
          </p>
          <ul className="impact-list">
            <li>Representative: Reflecting local supply chains and material realities.</li>
            <li>Transparent: Providing a clear view into the data's origin and processing.</li>
            <li>Contextualized: Tailored to the specific environmental and industrial conditions of the region.</li>
          </ul>
        </div>

        <div className="impact-section">
          <h2>The ICoMEC Platform</h2>
          <p>
            This platform exists to provide the data infrastructure necessary for sustainable growth. By integrating localized datasets with rigorous Life Cycle Assessment (LCA) principles, ICoMEC enables a deeper understanding of Indian construction material impacts.
          </p>
          <p>
            Our focus is on moving the industry toward a state where every choice, from material selection to policy drafting, is rooted in high-fidelity, contextualized data.
          </p>
        </div>

        <div className="impact-section">
          <h2>Impact Through Decision Confidence</h2>
          <p>
            At ICoMEC, we define and measure impact through a unique lens: the confidence with which a decision can be justified.
          </p>
          <p>
            Impact is not just a final carbon value; it is the reliability of the process that produced it.
          </p>
          <p>
            We empower architects, engineers, and policymakers to move from estimation to data-based decisions, ensuring that every climate-conscious development is backed by a transparent and rigorous framework.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Impact Page. */
export default ImpactPage;

